import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import RadioButton from '../../../../components/forms/RadioButton';
import Checkbox from '../../../../components/forms/Checkbox';
import StatusLabel from '../../../../components/forms/StatusLabel';

import {
  StopSellBrokerModel,
  stopSellEditRoles,
  StopSellModel,
  StopSellProductModel,
  StopSellRuleType,
  StopSellRuleTypeOptions
} from '../../../../api/pricing/pricing.types';
import { DATE_FORMAT, SelectedOption, StatusType } from '../../../../api/api.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { CarClassModel, DeskModel, ProductModel } from '../../../../api/core/core.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { StopSellActionTypes } from '../../types/stopsell.types';
import { addNewStopSell, deleteStopSell, editStopSell, getStopSell } from '../../actions/stopsell.actions';
import Select from '../../../../components/forms/Select';
import { getProductList } from '../../../setup/actions/product.actions';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { TwoColumnsSelectValueModel } from '../../../../components/forms/TwoColumnsSelect';
import { MultiSelect } from '../../../../components/forms/MultiSelect';
import { getBrokers, getCarClassesList, getLocationsList } from '../../../../helpers/listview.helper';
import CalendarDateRangePicker, { DateRangeInputNew } from '../../../../components/forms/CalendarDateRangePicker';
import dayjs from 'dayjs';
import { dateOrderCorrect, dateTimeOrderCorrect, formatDateTime } from '../../../../helpers/date.helper';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import { Link } from 'react-router-dom';
import { SupplierBrokerModel } from '../../../../api/admin/admin.types';
import { getSupplierBrokerList } from '../../../admin/actions/supplierbroker.actions';

interface StopSellLocationProps {
  id: string;
}

interface StopSellDetailsProps extends RouteComponentProps<StopSellLocationProps>, WithTranslation {
  selectedStopSell: StopSellModel | null;
  getSelectedStopSell: (id: number) => Promise<any>;
  editStopSell: (id: number, request: StopSellModel) => void;
  addNewStopSell: (request: StopSellModel) => void;
  deletedStopSell: (id: number) => void;
  getAllCarClasses: () => void;
  getDeskList: () => void;
  getBrokerList: () => void;
  getProductList: () => void;
  isNew?: boolean;
  postSuccess: boolean | null;
  carClasses: CarClassModel[];
  desks: DeskModel[];
  brokers: SupplierBrokerModel[];
  products: ProductModel[];
  lastFetch: number | null;
  user: UserModel | null;
}

interface StopSellDetailsState {
  isModalOpen: boolean;
  isEdit: boolean;
  selectedDesks: any[];
  selectedCarClasses: any[];
  selectedProducts: number[];
  selectedBrokers: number[];
}

class StopSellDetails extends React.Component<StopSellDetailsProps, StopSellDetailsState> {

  readonly state = {
    isModalOpen: false,
    isEdit: false,
    selectedDesks: [],
    selectedCarClasses: [],
    selectedProducts: [],
    selectedBrokers: []
  };

  form = FormBuilder.group({
    id: [ null ],
    comment: [ '' ],
    ruleType: [ StopSellRuleType.OVERLAP, Validators.required ],
    validFrom: [ null, Validators.required ],
    validTo: [ null, Validators.required ],
    validFromTime: [ '00:00', Validators.required ],
    validToTime: [ '23:59', Validators.required ],
    timeToPickup: [ null ],
    locationsSelection: [ 'all' ],
    carClassSelection: [ 'all' ],
    locations: [ { items: [], selected: [] } ],
    carClasses: [ { items: [], selected: [] } ],
    brokerSelection: [ 'all' ],
    productSelection: [ 'all' ],
    brokers: [ { items: [], selected: [] } ],
    products: [ { items: [], selected: [] } ],
    active: [ true ],
    considerOnlyDeskOpeningHours: [ false ],
    rentalDays: [ null ]
  });

  async componentDidMount() {
    if (!this.props.carClasses.length) {
      this.props.getAllCarClasses();
    }
    if (!this.props.products.length) {
      this.props.getProductList();
    }
    if (!this.props.desks.length) {
      this.props.getDeskList();
    }
    if (!this.props.brokers.length) {
      this.props.getBrokerList();
    }

    const { id } = this.props.match.params;
    if (id) {
      await this.props.getSelectedStopSell(Number(id));
    }
    if (this.props.desks.length) {
      this.assignDesksToForm(this.props.desks);
    }
    if (this.props.carClasses.length) {
      this.assignCarClassesToForm(this.props.carClasses);
    }
    if (this.props.brokers.length) {
      this.assignBrokersToFrom(this.props.brokers);
    }
    if (this.props.products.length) {
      this.assignProductsToForm(this.props.products);
    }
  }

  componentDidUpdate(prevProps: StopSellDetailsProps) {
    if ((this.props.isNew) && this.props.postSuccess && this.props.selectedStopSell) {
      this.props.history.push(`/rates/stopsells/${this.props.selectedStopSell.id}/view`);
    }
    if (this.props.selectedStopSell && (!prevProps.selectedStopSell || this.props.selectedStopSell !== prevProps.selectedStopSell)) {
      this.updateFormValue();
    }

    if (this.props.carClasses.length !== prevProps.carClasses.length) {
      this.assignCarClassesToForm(this.props.carClasses);
    }
    if (this.props.desks.length !== prevProps.desks.length) {
      this.assignDesksToForm(this.props.desks);
    }
    if (this.props.brokers.length !== prevProps.brokers.length) {
      this.assignBrokersToFrom(this.props.brokers);
    }
    if (this.props.products.length !== prevProps.products.length) {
      this.assignProductsToForm(this.props.products);
    }
  }

  updateFormValue = () => {
    if (!this.props.selectedStopSell) {
      return;
    }
    this.form.patchValue({
      id: this.props.selectedStopSell.id,
      comment: this.props.selectedStopSell.comment,
      ruleType: this.props.selectedStopSell.ruleType ? this.props.selectedStopSell.ruleType : StopSellRuleType.OVERLAP,
      timeToPickup: this.props.selectedStopSell.timeToPickup,
      validFrom: dayjs(this.props.selectedStopSell.validFrom),
      validTo: dayjs(this.props.selectedStopSell.validTo),
      validFromTime: (this.props.selectedStopSell.validFrom).split('T')[ 1 ].slice(0, 5),
      validToTime: (this.props.selectedStopSell.validTo).split('T')[ 1 ].slice(0, 5),
      locationsSelection: this.props.selectedStopSell.stopSellDesks.length ? 'selection' : 'all',
      carClassSelection: this.props.selectedStopSell.stopSellCarClasses.length ? 'selection' : 'all',
      locations: this.props.selectedStopSell ? {
        ...this.form.controls.locations.value,
        selected: this.props.selectedStopSell.stopSellDesks.map((d) => d.deskCode)
      } : this.form.controls.locations.value,
      carClasses: this.props.selectedStopSell ? {
        ...this.form.controls.carClasses.value,
        selected: this.props.selectedStopSell.stopSellCarClasses.map((c) => c.acrissCode)
      } : this.form.controls.carClasses.value,
      brokerSelection: this.props.selectedStopSell.stopSellBrokers.length ? 'selection' : 'all',
      productSelection: this.props.selectedStopSell.stopSellProducts.length ? 'selection' : 'all',
      brokers: this.props.selectedStopSell ? {
        ...this.form.controls.brokers.value,
        selected: this.props.selectedStopSell.stopSellBrokers.map((b) => b.brokerId)
      } : this.form.controls.brokers.value,
      products: this.props.selectedStopSell ? {
        ...this.form.controls.products.value,
        selected: this.props.selectedStopSell.stopSellProducts.map((p) => p.productId)
      } : this.form.controls.products.value,
      active: this.props.selectedStopSell.statusType === StatusType.ACTIVE,
      considerOnlyDeskOpeningHours: this.props.selectedStopSell.considerOnlyDeskOpeningHours,
      createdByApi: this.props.selectedStopSell?.createdByApi ?? false,
      rentalDays: this.props.selectedStopSell.rentalDays
    });

    this.setState({
      selectedCarClasses: this.props.selectedStopSell.stopSellCarClasses.map((c) => c.acrissCode),
      selectedDesks: this.props.selectedStopSell.stopSellDesks.map((c) => c.deskCode),
      selectedBrokers: this.props.selectedStopSell.stopSellBrokers.map((b) => b.brokerId),
      selectedProducts: this.props.selectedStopSell.stopSellProducts.map((p) => p.productId)
    });
  };

  populateTimeSelect = () => {
    const times: { value: string; label: string }[] = [];
    for (let i = 0; i < 24; i += 1) {
      if (i < 10) {
        times.push({ value: (`0${String(i)}:00`), label: (`0${String(i)}:00`) });
        times.push({ value: (`0${String(i)}:30`), label: (`0${String(i)}:30`) });
      } else {
        times.push({ value: (`${String(i)}:00`), label: (`${String(i)}:00`) });
        times.push({ value: (`${String(i)}:30`), label: (`${String(i)}:30`) });
      }
    }

    return times;
  };

  assignDesksToForm = (desks: DeskModel[]) => {
    const prev = this.form.controls.locations.value as TwoColumnsSelectValueModel;
    desks.forEach((desk) => {
      prev.items.push({
        name: desk.name,
        sortName: desk.name,
        id: desk.id,
        deskCode: desk.code
      });
    });
    if (this.props.selectedStopSell) {
      this.props.selectedStopSell.stopSellDesks.forEach((d) => {
        prev.selected.push(d.deskCode);
      });
      this.setState({ selectedDesks: this.props.selectedStopSell.stopSellDesks.map((d) => d.deskCode) });
    }
    this.form.controls.locations.setValue(prev);
  };

  assignCarClassesToForm = (carClasses: CarClassModel[]) => {
    const prev = this.form.controls.carClasses.value as TwoColumnsSelectValueModel;
    const uniqueClasses = [];
    const seen = new Set();
    for (const item of carClasses) {
      if (!seen.has(item.acrissCode)) {
        seen.add(item.acrissCode);
        uniqueClasses.push(item);
      }
    }
    prev.items = uniqueClasses;
    if (this.props.selectedStopSell) {
      this.props.selectedStopSell.stopSellCarClasses.forEach((c) => {
        prev.selected.push(c.acrissCode);
      });
      this.setState({
        selectedCarClasses: this.props.selectedStopSell.stopSellCarClasses
          .map((c) => c.acrissCode)
      });
    }
  };

  assignBrokersToFrom = (brokers: SupplierBrokerModel[]) => {
    const form = this.form.controls.brokers.value as TwoColumnsSelectValueModel;
    brokers.forEach((broker) => {
      const obj: any = { ...broker };
      obj.brokerId = broker.id;
      form.items.push(obj);
    });

    if (this.props.selectedStopSell) {
      form.selected = this.props.selectedStopSell.stopSellBrokers.map((b) => b.brokerId);
      this.setState({
        selectedBrokers: this.props.selectedStopSell.stopSellBrokers.map((b) => b.brokerId)
      });
    }
  };

  assignProductsToForm = (products: ProductModel[]) => {
    const form = this.form.controls.products.value as TwoColumnsSelectValueModel;
    products.forEach((p) => {
      const obj: any = { ...p };
      obj.productId = p.id;
      form.items.push(obj);
    });

    if (this.props.selectedStopSell) {
      form.selected = this.props.selectedStopSell.stopSellProducts.map((p) => p.productId);
      this.setState({
        selectedProducts: this.props.selectedStopSell.stopSellProducts.map((p) => p.productId)
      });
    }
  };

  getLabel = (list: any[], value: string) => {
    const obj = list.find((select) => select.value === value);
    if (obj) {
      return obj.label;
    }

    return value;
  };

  onAllCarClassesSelect = () => {
    this.form.controls.carClasses.setValue({ ...this.form.controls.carClasses.value, selected: [] });
    this.setState({ selectedCarClasses: [] });
  };

  onAllLocationsSelect = () => {
    this.form.controls.locations.setValue({ ...this.form.controls.locations.value, selected: [] });
    this.setState({ selectedDesks: [] });
  };

  onAllProductsSelected = () => {
    this.form.controls.products.setValue({ ...this.form.controls.products.value, selected: [] });
    this.setState({ selectedProducts: [] });
  };

  onAllBrokersSelected = () => {
    this.form.controls.brokers.setValue({ ...this.form.controls.brokers.value, selected: [] });
    this.setState({ selectedBrokers: [] });
  };

  getBrokers = (brokers: StopSellBrokerModel[]) => {
    let brokerList;
    if (brokers.length === 0) {
      brokerList = this.props.t('v.stop_sells.all_brokers');
    } else {
      brokerList = (
        <ul className="details-list">
          {brokers.map((broker: StopSellBrokerModel) => {
            const fullBroker = this.props.brokers.find((d) => d.id === broker.brokerId);
            return (
              <li key={broker.brokerId}>
                {
                  fullBroker && fullBroker.name || broker.brokerId
                }
              </li>
            );
          })}
        </ul>
      );
    }
    return brokerList;
  };

  getProducts = (products: StopSellProductModel[]) => {
    let productList;
    if (products.length === 0) {
      productList = this.props.t('v.stop_sells.all_products');
    } else {
      productList = (
        <ul className="details-list">
          {products.map((product: StopSellProductModel) => {
            const fullProduct = this.props.products.find((d) => d.id === product.productId);
            return (
              <li
                key={product.productId}
                className="list-link-item"
                onClick={() => this.props.history.push(`/setup/products/${product.productId}/view`)}
              >
                {
                  fullProduct && fullProduct.name || product.productId
                }
              </li>
            );
          })}
        </ul>
      );
    }
    return productList;
  };

  handleLocationSelectValueChange = (v: any) => {
    this.setState({ selectedDesks: v.map((itm: any) => itm.deskCode) });
  };

  handleCarClassSelectValueChange = (v: any) => {
    this.setState({ selectedCarClasses: v.map((itm: any) => itm.acrissCode) });
  };

  handleProductValueChange = (v: any) => {
    this.setState({ selectedProducts: v.map((itm: any) => itm.productId) });
  };

  handleBrokerValueChange = (v: any) => {
    this.setState({ selectedBrokers: v.map((itm: any) => itm.brokerId) });
  };

  onDateRangeChange = (range: DateRangeInputNew, field: string) => {
    dateOrderCorrect({ startDate: range.startDate, endDate: range.endDate }, 'valid', this.form,
      true);
    dateTimeOrderCorrect({
      startDate: range.startDate,
      endDate: range.endDate, startTime: this.form.controls.validFromTime.value,
      endTime: this.form.controls.validToTime.value
    }, 'valid', this.form);
    this.form.patchValue({
      [ `${field}From` ]: range.startDate,
      [ `${field}To` ]: range.endDate
    });
  };

  onDeleteConfirm = async () => {
    if (!this.props.selectedStopSell?.createdByApi) {
      const { id } = this.props.match.params;
      await this.props.deletedStopSell(Number(id));
      this.props.history.push('/rates/stopsells');
    }
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid || this.props.selectedStopSell?.createdByApi) {
      return;
    }
    if (!dateOrderCorrect({ startDate: form.validFrom.value, endDate: form.validTo.value }, 'valid',
      this.form, true)) {
      return;
    }

    const startTime = form.validFromTime.value.value ? form.validFromTime.value.value : form.validFromTime.value;
    const endTime = form.validToTime.value.value ? form.validToTime.value.value : form.validToTime.value;
    const dateTimeCheck = dateTimeOrderCorrect({
      startDate: form.validFrom.value,
      endDate: form.validTo.value, startTime: startTime, endTime: endTime
    }, 'valid', this.form);
    if (dateTimeCheck.error) {
      return;
    }

    const request: StopSellModel = {
      comment: form.comment.value,
      validFrom: `${form.validFrom.value.format(DATE_FORMAT)}T${startTime}`,
      validTo: `${form.validTo.value.format(DATE_FORMAT)}T${endTime}`,
      timeToPickup: form.timeToPickup.value,
      ruleType: form.ruleType.value,
      stopSellDesks: this.state.selectedDesks.map((v: string) => ({ deskCode: v })),
      stopSellCarClasses: this.state.selectedCarClasses.map((v: string) => ({ acrissCode: v })),
      stopSellBrokers: this.state.selectedBrokers.map((v: number) => ({ brokerId: v })),
      stopSellProducts: this.state.selectedProducts.map((v: number) => ({ productId: v })),
      statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
      considerOnlyDeskOpeningHours: form.considerOnlyDeskOpeningHours.value,
      createdByApi: this.props.selectedStopSell?.createdByApi ?? false,
      rentalDays: form.rentalDays.value
    };

    if (this.props.selectedStopSell) {
      const { id } = this.props.match.params;
      this.props.editStopSell(Number(id), request);
      this.setState({ isEdit: false });

    } else {
      this.props.addNewStopSell(request);
    }
  };

  patchField = (option: SelectedOption, field: string) => {
    if (field === 'validFromTime') {
      dateTimeOrderCorrect({
        startDate: this.form.controls.validFrom.value,
        endDate: this.form.controls.validTo.value, startTime: option.value,
        endTime: this.form.controls.validToTime.value
      }, 'valid', this.form);
    } else if (field === 'validToTime') {
      dateTimeOrderCorrect({
        startDate: this.form.controls.validFrom.value,
        endDate: this.form.controls.validTo.value, startTime: this.form.controls.validFromTime.value,
        endTime: option.value
      }, 'valid', this.form);
    }
    this.form.patchValue({ [ field ]: option.value });
  };

  onCancel = () => {
    if (this.props.isNew) {
      this.props.history.push('/rates/stopsells/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  render() {
    if (!this.props.isNew && !this.props.selectedStopSell) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNew;
    const { t } = this.props;
    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/rates', label: 'Rates' },
              { path: '/rates/stopsells', label: 'Stop Sells' },
              { path: null, label: 'Stop Sell Details' }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{this.props.isNew ? t('page_title.add_new_stop_sell') :
                t('page_title.stop_sell_details')}</h1>
              {!!this.props.selectedStopSell && !showForm && this.props.user && !this.props.selectedStopSell.createdByApi && userCanAccessComponent(this.props.user, stopSellEditRoles)
                && (
                  <div>
                    <button
                      type="button"
                      className="btn btn-danger mr-2"
                      onClick={() => this.setState({ isModalOpen: true })}
                    >
                      {t('b.delete_Generic delete button').toUpperCase()}
                    </button>
                    <button type="button" className="btn btn-primary edit-button" onClick={() => this.onEdit()}>
                      {t('b.edit_Generic edit button').toUpperCase()}
                    </button>
                  </div>
                )}
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal pt-1" onSubmit={(e) => this.onSubmit(e)}>
                <SectionTitle title="STOP SELL"/>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>ID:</label>
                  </div>
                  <div
                    className="col-2 d-flex align-items-center justify-content-start padding-zero edit-form-row-input-margins">
                    {
                      this.props.selectedStopSell &&
                      (
                        <div className="form-control-static">
                          {this.props.selectedStopSell.id}
                        </div>
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.stop_sell.rule_type')}:</label>
                  </div>
                  <div
                    className="col-2 d-flex align-items-center justify-content-start padding-zero edit-form-row-input-margins">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          {this.getLabel(StopSellRuleTypeOptions, this.form.controls.ruleType.value)}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          strict={false}
                          name="ruleType"
                          render={(props) => (
                            <Select
                              value={StopSellRuleTypeOptions.find((select) => select.value === this.form.controls.ruleType.value)}
                              className={`mr-1 ${(this.form.submitted && !this.form.controls.ruleType.value) ?
                                'invalid' : ''}`}
                              disableMinWidth
                              placeholder={t('ph.stop_sell.rule_type')}
                              options={(StopSellRuleTypeOptions)}
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              label="rule type"
                              disableClear
                              onChange={(value) => this.patchField(value as SelectedOption, 'ruleType')}
                            />
                          )}
                        />
                      )
                    }
                  </div>
                </div>
                {this.form.controls.ruleType.value === StopSellRuleType.MIN_TIME_TO_PICKUP && (
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.stop_sell.time_to_pickup')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start">
                      {
                        this.props.selectedStopSell && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.timeToPickup.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="timeToPickup"
                            render={NumberInput}
                          />
                        )
                      }
                    </div>
                  </div>
                )}
                {this.form.controls.ruleType.value === StopSellRuleType.BLOCK_SHORT_RENTALS && (
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.stop_sell.rental_days')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start">
                      {
                        this.props.selectedStopSell && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.rentalDays.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="rentalDays"
                            render={NumberInput}
                          />
                        )
                      }
                    </div>
                  </div>
                )}
                {this.form.controls.ruleType.value === StopSellRuleType.MIN_TIME_TO_PICKUP && (
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.stop_sell.consider_only_desk_opening_hours')}:</label>
                    </div>
                    <div className="col-3 d-flex align-items-center justify-content-start">
                      <FieldControl
                        strict={false}
                        name="considerOnlyDeskOpeningHours"
                        render={(props) => <Checkbox {...props} id="considerOnlyDeskOpeningHours"
                                                     className="mt-1" disabled={!showForm}></Checkbox>}
                      />
                    </div>
                  </div>
                )
                }
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.stop_sell.comment')}:</label>
                  </div>
                  <div
                    className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          {this.form.controls.comment.value}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="comment"
                          render={TextInput}
                        />
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.stop_sell.valid_period')}:</label>
                  </div>
                  {(this.props.selectedStopSell && !showForm)
                    && (
                      <div className="d-flex col-8">
                        <div>
                          <div className="d-flex">
                            <div className="col-6 pl-0">
                              {this.form.controls.validFrom.value && this.form.controls.validFrom.value.format('DD.MM.YYYY')}
                            </div>
                            <div className="col-6">
                              {this.form.controls.validFromTime.value}
                            </div>
                          </div>
                          <div className="d-flex">
                            <div className="col-6 pl-0">
                              {this.form.controls.validTo.value && this.form.controls.validTo.value.format('DD.MM.YYYY')}
                            </div>
                            <div className="col-6">
                              {this.form.controls.validToTime.value}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  <div className="col-4 d-flex align-items-center justify-content-start input_two_items_wide
                     padding-zero edit-form-row-input-margins">
                    {
                      showForm && (
                        <div className="col-12 pl-0 pr-0">
                          <div className="col-12 row mr-0 pr-0 ml-0 pl-0 justify-content-between"
                               style={{ paddingBottom: '2px' }}>
                            <div
                              className="col-6 row-max-width-item-2 padding-zero edit-form-row-input-margins ml-0 mr-0">
                              <CalendarDateRangePicker
                                startDate={this.form.controls.validFrom.value}
                                endDate={this.form.controls.validTo.value}
                                isStartDateInput
                                isInvalid={this.form.controls.validFrom.invalid && this.form.submitted}
                                onChange={(val) => this.onDateRangeChange(val, 'valid')}
                                id="1"
                                isDateTimeInput
                              />
                            </div>
                            <div className="col-6 pr-0 pl-1">
                              <FieldControl
                                strict={false}
                                name="validFromTime"
                                render={(props) => (
                                  <Select
                                    defaultValue={{
                                      value: this.form.controls.validFromTime.value,
                                      label: String(this.form.controls.validFromTime.value)
                                    }}
                                    className={`mr-1 ${this.form.controls.validFromTime.invalid ? 'invalid' : ''}`}
                                    disableMinWidth
                                    placeholder="Choose start time"
                                    options={(this.populateTimeSelect())}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    label="valid from time"
                                    onChange={(value) => this.patchField(value as SelectedOption, 'validFromTime')}
                                  />
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-12 row mr-0 pr-0 ml-0 pl-0 justify-content-between">
                            <div
                              className="col-6 row-max-width-item-2 padding-zero edit-form-row-input-margins ml-0 mr-0">
                              <CalendarDateRangePicker
                                startDate={this.form.controls.validFrom.value}
                                endDate={this.form.controls.validTo.value}
                                isStartDateInput={false}
                                isInvalid={this.form.controls.validTo.invalid && this.form.submitted}
                                onChange={(val) => this.onDateRangeChange(val, 'valid')}
                                id="2"
                                isDateTimeInput
                              />
                            </div>
                            <div className="col-6 pl-1 pr-0">
                              <FieldControl
                                strict={false}
                                name="validToTime"
                                render={(props) => (
                                  <Select
                                    defaultValue={{
                                      value: this.form.controls.validToTime.value,
                                      label: String(this.form.controls.validToTime.value)
                                    }}
                                    className={`mr-1 ${this.form.controls.validToTime.invalid ? 'invalid' : ''}`}
                                    disableMinWidth
                                    placeholder="Choose end time"
                                    options={(this.populateTimeSelect())}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    label="end time"
                                    onChange={(value) => this.patchField(value as SelectedOption, 'validToTime')}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    {showForm && (
                      <label className="mandatory-field">* </label>
                    )}
                    <label>{t('l.stop_sell.status')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          <StatusLabel status={this.props.selectedStopSell.statusType}/>
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <FieldControl
                          name="active"
                          render={(props) => <Checkbox {...props} id="statusField"
                                                       className="mt-1">{t('text.status.active')}</Checkbox>}
                        />
                      )
                    }
                  </div>
                </div>

                {this.props.selectedStopSell?.createdByApi && (
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>Created by API:</label>
                    </div>
                    <div className="col-3 d-flex align-items-center justify-content-start">
                      <FieldControl
                        strict={false}
                        name="createdByApi"
                        render={(props) => <Checkbox {...props} id="createdByApi" disabled={true}
                                                     className="mt-1"></Checkbox>}
                      />
                    </div>
                  </div>)
                }
                <SectionTitle title={t('section_title.stop_sell.desks').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4"/>
                  <div className="col-8 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          {getLocationsList(
                            this.props.selectedStopSell.stopSellDesks, this.props.desks, false,
                            this.props.history
                          )}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="locationsSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllLocationsSelect}
                                  fieldValue="all"
                                  id="loc_all"
                                >
                                  {t('l.selection.all')}
                                </RadioButton>
                              )}
                            />
                            <FieldControl
                              strict={false}
                              name="locationsSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  fieldValue="selection"
                                  id="loc_sel"
                                >
                                  {t('l.selection.selected')}
                                </RadioButton>
                              )}
                            />
                          </div>
                          {
                            this.form.controls.locationsSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="locations"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="deskCode"
                                    sortAttributeName="name"
                                    handleValueChange={(val: any) => this.handleLocationSelectValueChange(val)}
                                    filterAttributes={[ 'name', 'deskCode' ]}
                                    primaryAtr="name"
                                    secondaryAtr="deskCode"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      )
                    }
                  </div>
                </div>
                <SectionTitle title={t('section_title.stop_sell.car_classes').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4"/>
                  <div className="col-8 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          {getCarClassesList(
                            this.props.selectedStopSell.stopSellCarClasses,
                            this.props.carClasses,
                            this.props.t('v.all_car_classes'),
                            this.props.history
                          )}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="carClassSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllCarClassesSelect}
                                  fieldValue="all"
                                  id="car_all"
                                >
                                  {t('l.selection.all')}
                                </RadioButton>
                              )}
                            />
                            <FieldControl
                              strict={false}
                              name="carClassSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  fieldValue="selection"
                                  id="car_sel"
                                >
                                  {t('l.selection.selected')}
                                </RadioButton>
                              )}
                            />
                          </div>
                          {
                            this.form.controls.carClassSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="carClasses"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="acrissCode"
                                    sortAttributeName="acrissCode"
                                    handleValueChange={(val: any) => this.handleCarClassSelectValueChange(val)}
                                    filterAttributes={[ 'acrissCode' ]}
                                    primaryAtr="acrissCode"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      )
                    }
                  </div>
                </div>
                <SectionTitle title={t('section_title.stop_sell.products').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4"/>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          {this.getProducts(
                            this.props.selectedStopSell.stopSellProducts
                          )}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="productSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllProductsSelected}
                                  fieldValue="all"
                                  id="prod_all"
                                >
                                  {t('l.selection.all')}
                                </RadioButton>
                              )}
                            />
                            <FieldControl
                              strict={false}
                              name="productSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  fieldValue="selection"
                                  id="prod_sel"
                                >
                                  {t('l.selection.selected')}
                                </RadioButton>
                              )}
                            />
                          </div>
                          {
                            this.form.controls.productSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="products"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="productId"
                                    sortAttributeName="name"
                                    handleValueChange={(val: any) => this.handleProductValueChange(val)}
                                    filterAttributes={[ 'name' ]}
                                    secondaryAtr="name"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      )
                    }
                  </div>
                </div>
                <SectionTitle title={t('section_title.stop_sell.brokers').toUpperCase()}/>
                <div className="form-group row">
                  <div className="col-4"/>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    {
                      this.props.selectedStopSell && !showForm
                      && (
                        <div className="form-control-static">
                          {getBrokers(
                            this.props.selectedStopSell.stopSellBrokers.map((stopSellBroker) => ({ id: stopSellBroker.brokerId })),
                            this.props.brokers,
                            this.props.t('v.stop_sells.all_brokers')
                          )}
                        </div>
                      )
                    }
                    {
                      showForm
                      && (
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="brokerSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllBrokersSelected}
                                  fieldValue="all"
                                  id="bro_all"
                                >
                                  {t('l.selection.all')}
                                </RadioButton>
                              )}
                            />
                            <FieldControl
                              strict={false}
                              name="brokerSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  fieldValue="selection"
                                  id="bro_sel"
                                >
                                  {t('l.selection.selected')}
                                </RadioButton>
                              )}
                            />
                          </div>
                          {
                            this.form.controls.brokerSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="brokers"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="brokerId"
                                    sortAttributeName="name"
                                    handleValueChange={(val: any) => this.handleBrokerValueChange(val)}
                                    filterAttributes={[ 'name' ]}
                                    secondaryAtr="name"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      )
                    }
                  </div>
                </div>
                {showForm && (
                  <div className="row">
                    <div className="col-12 text-right">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-lg mr-2"
                        onClick={this.onCancel}
                      >
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}
                      </button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                        {t('b.save_Generic save button translation').toUpperCase()}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          />
          {this.props.selectedStopSell && !showForm && (
            <>
              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-8">
                  <div className="d-flex row info-container">
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedStopSell.createdBy ?? 'System'}{this.props.selectedStopSell.createdAt ? ` @ ${formatDateTime(this.props.selectedStopSell.createdAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedStopSell.modifiedBy ?? 'System'}{this.props.selectedStopSell.modifiedAt ? ` @ ${formatDateTime(this.props.selectedStopSell.modifiedAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <Link to={`/rates/stopsells/${this.props.selectedStopSell.id}/audit`}
                            className="btn btn-text-only"
                            target="_blank"
                            rel="noopener noreferrer">
                        {t('b.car_model.view_audit_log')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <ConfirmationModal
          title={t('modal_text.stop_sell.confirm_removal.title')}
          content={t('modal_text.stop_sell.confirm_removal.content')}
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedStopSell: state.rates.stopSell.selectStopSell,
  postSuccess: state.rates.stopSell.postSuccess,
  desks: state.setup.desks.desks,
  carClasses: state.core.carClasses.acrissCodes,
  products: state.setup.products.products,
  brokers: state.admin.supplierBrokers.supplierBrokers,
  lastFetch: state.rates.stopSell.lastFetch,
  user: state.authentication.user
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, StopSellActionTypes>
) => ({
  getSelectedStopSell: (id: number) => dispatch(getStopSell(id)),
  editStopSell: (id: number, request: StopSellModel) => dispatch(editStopSell(id, request)),
  addNewStopSell: (request: StopSellModel) => dispatch(addNewStopSell(request)),
  deletedStopSell: (id: number) => dispatch(deleteStopSell(id)),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getDeskList: () => dispatch(getDeskList()),
  getBrokerList: () => dispatch(getSupplierBrokerList()),
  getProductList: () => dispatch(getProductList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(StopSellDetails));
